import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useCookieConsentStore } from '@/stores/cookieConsentStore';
import { marketing } from './content';
import { usePublicLanguage } from './usePublicLanguage';
import './marketing.css';

export function PublicShell({
  children,
  title,
  description,
}: {
  children: ReactNode;
  title?: string;
  description?: string;
}) {
  const lang = usePublicLanguage();
  const c = marketing[lang];
  const location = useLocation();
  const { i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const authenticated = useAuthStore((s) => s.isAuthenticated);
  const resetConsent = useCookieConsentStore((s) => s.resetConsent);
  const suffix = location.pathname.replace(/^\/(fr|en)(?=\/|$)/, '').replace(/\/$/, '');
  const pagePath = suffix === '/' ? '' : suffix;
  const home = `/${lang}`;

  useEffect(() => {
    void i18n.changeLanguage(lang);
    document.title = title ? `${title} · NeoKanban` : `NeoKanban — ${c.title} ${c.emphasis}`;
    const desc = description || c.description;
    const metas: [string, string, string][] = [
      ['name', 'description', desc],
      ['property', 'og:title', document.title],
      ['property', 'og:description', desc],
      ['property', 'og:locale', lang === 'fr' ? 'fr_FR' : 'en_GB'],
      ['property', 'og:url', `${window.location.origin}/${lang}${pagePath}`],
    ];
    const added: HTMLElement[] = [];
    for (const [attribute, key, value] of metas) {
      let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.append(element);
        added.push(element);
      }
      element.content = value;
    }
    for (const locale of [lang, 'fr', 'en', 'x-default']) {
      const link = document.createElement('link');
      const canonical = added.some(
        (el) => el.tagName === 'LINK' && el.getAttribute('rel') === 'canonical'
      );
      link.rel = canonical ? 'alternate' : 'canonical';
      if (canonical) link.hreflang = locale;
      link.href = `${window.location.origin}/${locale === 'x-default' ? 'en' : locale}${pagePath}`;
      document.head.append(link);
      added.push(link);
    }
    return () => added.forEach((el) => el.remove());
  }, [lang, title, description, c, pagePath, i18n]);

  useEffect(() => {
    setMenuOpen(false);
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  const legalLinks = [
    ['legal-notice', c.notice],
    ['terms', c.terms],
    ['sales-terms', c.sales],
    ['privacy', c.privacy],
    ['ai-transparency', c.ai],
  ];
  return (
    <div className="marketing">
      <a className="mk-skip" href="#main-content">
        {c.skip}
      </a>
      <header className="mk-header">
        <div className="mk-container mk-nav">
          <Link className="mk-brand" to={home} aria-label="NeoKanban">
            <span className="mk-mark" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            NeoKanban<span className="mk-brand-dot">.</span>
          </Link>
          <nav className="mk-desktop-nav" aria-label={c.menu}>
            {c.nav.map((label, i) => (
              <a key={label} href={`${home}#${['features', 'hosting', 'faq'][i]}`}>
                {label}
              </a>
            ))}
          </nav>
          <div className="mk-nav-actions">
            <nav className="mk-language" aria-label="Language / Langue">
              {(['fr', 'en'] as const).map((l) => (
                <Link
                  key={l}
                  lang={l}
                  hrefLang={l}
                  aria-current={lang === l ? 'page' : undefined}
                  to={`/${l}${pagePath}${location.search}${location.hash}`}
                >
                  {l.toUpperCase()}
                </Link>
              ))}
            </nav>
            <Link className="mk-login" to={authenticated ? '/dashboard' : '/login'}>
              {authenticated ? c.dashboard : c.login}
              <ArrowUpRight size={16} aria-hidden="true" />
            </Link>
            <button
              className="mk-menu-toggle"
              aria-label={c.menu}
              aria-expanded={menuOpen}
              aria-controls="public-navigation"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav id="public-navigation" className="mk-mobile-nav" aria-label={c.menu}>
            {c.nav.map((label, i) => (
              <a
                onClick={() => setMenuOpen(false)}
                key={label}
                href={`${home}#${['features', 'hosting', 'faq'][i]}`}
              >
                {label}
              </a>
            ))}
            <Link to={authenticated ? '/dashboard' : '/login'}>
              {authenticated ? c.dashboard : c.login}
            </Link>
          </nav>
        )}
      </header>
      {children}
      <footer className="mk-footer">
        <div className="mk-container mk-footer-grid">
          <div>
            <Link className="mk-brand" to={home}>
              NeoKanban<span className="mk-brand-dot">.</span>
            </Link>
            <p>{c.footer}</p>
            <p className="mk-small">
              {c.company}
              <br />
              {c.b2b}
            </p>
          </div>
          <nav aria-label={c.product}>
            <h2>{c.product}</h2>
            <a href={`${home}#features`}>{c.nav[0]}</a>
            <Link to={`${home}/pricing`}>{c.pricing}</Link>
            <a href={`${home}#hosting`}>{c.nav[1]}</a>
            <a href="mailto:contact@sponge-theory.ai">Contact</a>
          </nav>
          <nav aria-label={c.legal}>
            <h2>{c.legal}</h2>
            {legalLinks.map(([path, label]) => (
              <Link key={path} to={`${home}/${path}`}>
                {label}
              </Link>
            ))}
            <button onClick={resetConsent}>{c.cookies}</button>
          </nav>
        </div>
        <div className="mk-container mk-copyright">
          © {new Date().getFullYear()} Sponge Theory. {c.rights}
          <span>France · FR / EN</span>
        </div>
      </footer>
    </div>
  );
}
